import React from "react";
import { Link } from "react-router-dom";
import {
  Heart, Flame, Truck, Building2, Sparkles, FileText,
  Flower2, Frame, ShieldCheck, HandHeart, Brush, ArrowRight, Phone
} from "lucide-react";
import { SITE, IMAGES } from "@/lib/site";
import SectionTitle from "@/components/SectionTitle";
import PriceBanner from "@/components/PriceBanner";
import usePageMeta from "@/hooks/usePageMeta";

const SERVICES = [
  { icon: Heart, title: "Organisation complète d'obsèques", desc: "Cérémonies civiles et religieuses, conformément aux volontés du défunt et de la famille." },
  { icon: Flame, title: "Inhumation / Crémation", desc: "Démarches, lieux de cérémonie, urnes ou caveaux : nous coordonnons chaque détail." },
  { icon: Truck, title: "Transport du défunt", desc: "Avant et après mise en bière, en France et à l'international, dans le plus grand respect." },
  { icon: Building2, title: "Chambre funéraire", desc: "Un lieu intime et apaisé pour le recueillement, accessible à toute heure." },
  { icon: Sparkles, title: "Soins de présentation", desc: "Toilette, habillage, soins esthétiques : pour offrir un dernier regard digne et serein." },
  { icon: FileText, title: "Démarches administratives", desc: "Mairie, médecins, organismes : nous prenons en charge les formalités à votre place." },
  { icon: Flower2, title: "Marbrerie funéraire", desc: "Tombes, pierres, monuments, rénovations. Travail soigné et durable." },
  { icon: Frame, title: "Ornements", desc: "Plaques, fleurs, urnes, gravures personnalisées et compositions florales sur mesure." },
  { icon: ShieldCheck, title: "Contrats obsèques / Prévoyance", desc: "Anticiper, protéger ses proches et figer le tarif : nous vous conseillons sans engagement." },
  { icon: HandHeart, title: "Accompagnement des familles", desc: "Écoute, conseils, soutien moral et logistique tout au long de la cérémonie." },
  { icon: Brush, title: "Nettoyage et entretien de sépultures", desc: "Pour que la mémoire reste belle, nous entretenons les tombes avec soin et régularité." },
];

export default function ServicesPage() {
  usePageMeta(
    "Nos services funéraires à Baho",
    "Organisation complète d'obsèques, inhumation, crémation, transport, chambre funéraire, soins, démarches administratives, marbrerie, ornements, prévoyance — Pompes Funèbres Lumière des Nations à Baho (66540)."
  );
  return (
    <div data-testid="services-page">
      {/* HEADER */}
      <section className="relative bg-black text-white py-28 lg:py-40">
        <div className="absolute inset-0 opacity-30">
          <img src={IMAGES.lily} alt="" className="w-full h-full object-cover grayscale" />
        </div>
        <div className="relative container-soft">
          <p className="overline text-white/60">Nos services</p>
          <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-4 max-w-4xl tracking-tight leading-[1.02]">
            Une prise en charge <em className="italic font-light">complète</em>, du premier appel jusqu'au dernier hommage.
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-lg leading-relaxed">
            Onze services pensés pour soulager votre peine, simplifier les démarches
            et rendre l'hommage à la hauteur du souvenir.
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="py-24 lg:py-32">
        <div className="container-soft">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={i}
                  data-testid={`service-card-${i}`}
                  className="bg-white p-10 hover:bg-neutral-50 transition-colors duration-500"
                >
                  <div className="flex items-center justify-between">
                    <Icon size={28} strokeWidth={1.25} />
                    <span className="font-serif-display text-xl text-black/30">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-serif-display text-2xl mt-8 leading-tight">{s.title}</h3>
                  <p className="mt-4 text-neutral-600 leading-relaxed">{s.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <PriceBanner />

      {/* CTA */}
      <section className="py-24 lg:py-32">
        <div className="container-soft text-center">
          <SectionTitle
            overline="Besoin d'un conseil ?"
            title="Parlons de vos souhaits, simplement."
            kicker="Un échange téléphonique pour comprendre votre situation et proposer la solution la plus juste."
            align="center"
          />
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={SITE.phoneHref}
              data-testid="services-cta-call"
              className="inline-flex items-center justify-center gap-3 bg-black text-white px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-neutral-800 transition-all duration-300"
            >
              <Phone size={18} strokeWidth={1.5} /> Nous appeler
            </a>
            <Link
              to="/contact"
              data-testid="services-cta-contact"
              className="inline-flex items-center justify-center gap-2 border border-black px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-black hover:text-white transition-all duration-300"
            >
              Demander un devis <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
